import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { StorageItem } from '@vsolv/packages/storage/domain';
import { EditDocumentDialog } from '@vsolv/packages/storage/web';
import { BadgeColumn, ConnectedTableComponent, IconButtonColumn, TableColumn, TextColumn } from '@vsolv/vectors/table';
import { Distributor } from '@wsphere/distributors/domain';
import { SecurityService } from '@wsphere/staff/web';
import moment from 'moment';
import { catchError, distinctUntilChanged, map, Observable, of, ReplaySubject, startWith, switchMap } from 'rxjs';

@Component({
  selector: 'ws-distributor-documents-page',
  templateUrl: './documents.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DistributorDocumentsPage implements OnDestroy {
  private securitySvc = inject(SecurityService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  @ViewChild(ConnectedTableComponent) protected table?: ConnectedTableComponent<StorageItem.Model>;

  @ViewChild(EditDocumentDialog) private edit_dialog?: EditDocumentDialog;

  private distributor$ = new ReplaySubject<Distributor.Model | null>(1);
  @Input() set distributor(distributor: Distributor.Model | null) {
    this.distributor$.next(distributor);
  }

  protected endpoint$ = this.distributor$.pipe(
    map(distributor => (distributor ? `/api/distributors/${distributor.id}/documents` : null))
  );

  protected hasEditAccess$ = this.distributor$.pipe(
    map(distributor => distributor?.permissionKey ?? null),
    distinctUntilChanged(),
    switchMap(key => this.securitySvc.hasAccess('dist_EditDocuments', key ? [key] : null)),
    catchError(() => of(false))
  );

  protected hasUploadAccess$ = this.distributor$.pipe(
    map(distributor => distributor?.permissionKey ?? null),
    distinctUntilChanged(),
    switchMap(key => this.securitySvc.hasAccess('dist_UploadDocuments', key ? [key] : null)),
    catchError(() => of(false))
  );

  protected columns$: Observable<TableColumn<StorageItem.Model>[]> = this.hasEditAccess$.pipe(
    startWith(false),
    map(hasEditAccess => [
      new TextColumn<StorageItem.Model>({ header: 'Uploaded', fitContent: true, sort: 'DESC' }, item => ({
        text: moment(item.created).format('L'),
        tooltip: {
          title: 'Document details',
          subtitle: `
        <p class="-mt-1 italic font-normal">${item.id}</p>

        <p class="mt-2">Uploaded</p>
        <p class="font-normal">${item.created.toISOString()}</p>

        <p class="mt-2">Last modified</p>
        <p class="font-normal">${item.modified.toISOString()}</p>
      `,
        },
      })),

      new BadgeColumn<StorageItem.Model>({ header: 'File type', fitContent: true }, item => {
        if (item.mimeType === 'application/pdf') {
          return { text: 'pdf', theme: 'danger' };
        }

        const type = item.mimeType.split('/')[0];
        if (['image', 'audio', 'video'].includes(type)) {
          return { text: type, theme: 'info' };
        }

        return { text: 'other', theme: 'default' };
      }),

      new TextColumn<StorageItem.Model>({ header: 'Title' }, item => ({
        text: item.title,
        classes: 'font-semibold max-w-xs truncate',
        tooltip: item.description ? { title: item.title, subtitle: item.description } : undefined,
      })),

      new IconButtonColumn<StorageItem.Model>(
        { fitContent: true, stickyEnd: true, customCellClasses: '!pr-0', customHeaderClasses: '!pr-0' },
        item => ({
          extraClasses: 'text-themed-500',
          icon: 'download-cloud-01',
          theme: 'default',
          rounded: true,
          type: 'clear',
          size: 'sm',

          href: item.downloadUrl ?? undefined,
          disabled: !item.downloadUrl,
        })
      ),

      new IconButtonColumn<StorageItem.Model>(
        {
          fitContent: true,
          stickyEnd: true,
          customCellClasses: hasEditAccess ? '!px-0' : '!pl-0',
          customHeaderClasses: hasEditAccess ? '!px-0' : '!pl-0',
        },
        item => {
          const url = item.downloadUrl ? new URL(item.downloadUrl) : null;
          url?.searchParams.set('embedded', 'true');

          return {
            extraClasses: 'text-themed-500',
            icon: 'link-external-01',
            theme: 'default',
            rounded: true,
            type: 'clear',
            size: 'sm',

            href: url?.toString() ?? undefined,
            disabled: !url,
          };
        }
      ),

      ...(hasEditAccess
        ? [
            new IconButtonColumn<StorageItem.Model>(
              { fitContent: true, stickyEnd: true, customCellClasses: '!px-0', customHeaderClasses: '!px-0' },
              item => {
                return {
                  extraClasses: 'text-themed-500',
                  theme: 'default',
                  icon: 'edit-02',
                  rounded: true,
                  type: 'clear',
                  size: 'sm',

                  click: () => this.openEditDialog(item),
                };
              }
            ),
          ]
        : []),
    ])
  );

  protected itemToEdit: StorageItem.Model | null = null;
  private openEditDialog(item: StorageItem.Model) {
    this.itemToEdit = item;
    setTimeout(() => this.edit_dialog?.open());
  }

  protected refresh() {
    this.table?.dataSource?.refresh();
    setTimeout(() => this.changeDetectorRef.markForCheck());
  }

  ngOnDestroy() {
    this.distributor$.complete();
  }
}
